import { useReducer } from 'react';
import type { AgentId } from '../lib/schema';

export type CameraMode = 'free' | 'follow' | 'cinema' | 'record';

export interface CameraState {
  mode: CameraMode;
  focus: AgentId | null;
  zoom: 'wide' | 'medium' | 'close';
  crt: boolean;
  hudVisible: boolean;
  theme: 'day' | 'night';
}

const INITIAL: CameraState = {
  mode: 'free',
  focus: null,
  zoom: 'wide',
  crt: true,
  hudVisible: true,
  theme: 'day',
};

export type CameraAction =
  | { type: 'SET_MODE'; mode: CameraMode }
  | { type: 'FOCUS'; agentId: AgentId | null }
  | { type: 'TOGGLE_CRT' }
  | { type: 'TOGGLE_HUD' }
  | { type: 'TOGGLE_THEME' }
  | { type: 'CYCLE_ZOOM' };

function reducer(state: CameraState, action: CameraAction): CameraState {
  switch (action.type) {
    case 'SET_MODE':
      return { ...state, mode: action.mode, hudVisible: action.mode === 'record' ? false : state.hudVisible };
    case 'FOCUS':
      return { ...state, focus: action.agentId, mode: action.agentId ? 'follow' : 'free' };
    case 'TOGGLE_CRT':
      return { ...state, crt: !state.crt };
    case 'TOGGLE_HUD':
      return { ...state, hudVisible: !state.hudVisible };
    case 'TOGGLE_THEME':
      return { ...state, theme: state.theme === 'day' ? 'night' : 'day' };
    case 'CYCLE_ZOOM': {
      const order: CameraState['zoom'][] = ['wide', 'medium', 'close'];
      const idx = order.indexOf(state.zoom);
      return { ...state, zoom: order[(idx + 1) % order.length] };
    }
  }
}

export function useCinematicCamera() {
  return useReducer(reducer, INITIAL);
}
